import { Center, getThemeColor, useMantineTheme } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./icon.module.scss";
import type { IconFactory, IProps, ISkeletonProps } from "./icon.types";
import classnames from "classnames";
import { Skeleton } from "../skeleton";
import { componentWrapper } from "../../utilities";

/**
 * Icon component.
 * @param props.size Icon size.
 * @param props.icon Icon definition; if omitted, the component renders nothing.
 * @param props.color Icon colour. Accepts theme colour names (e.g. "error") like Mantine
 * components do — FontAwesome itself only understands CSS, so names are resolved here; raw CSS
 * values pass through unchanged.
 * @returns {JSX.Element | null} Icon component, or null when no icon is provided.
 */
const Icon = componentWrapper<IconFactory, IProps>("Icon", ({ size, className, color, icon, ...props }, ref) => {
    const theme = useMantineTheme();

    if (icon == null) {
        return null;
    }

    return (
        <Center ref={ref}>
            <FontAwesomeIcon
                fixedWidth
                color={color === undefined ? undefined : getThemeColor(color, theme)}
                data-size={size}
                className={classnames(styles["icon"], className)}
                icon={icon}
                {...props}
            />
        </Center>
    );
});

/**
 * Icon skeleton component.
 * @param props.size Icon size.
 * @returns {JSX.Element} Icon skeleton component.
 */
const IconSkeleton = ({ size, className, ...props }: ISkeletonProps) => {
    return <Skeleton circle data-size={size} className={styles["skeleton"]} {...props} />;
};

Icon.Skeleton = IconSkeleton;

export { Icon, IconSkeleton };
